import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router';
import { useAuthStore } from '@/features/auth/auth.store';
import { GiftCardValueService } from '../gift-card-value.services';
import type { IGiftCardValue } from '../gift-card-value.types';
import {
  toGiftCardValueOptions,
  type GiftCardValueValues,
} from '../gift-card-value.utils';
import { giftCardValueValidationSchema } from '../gift-card-value.validations';
import { usePresetList, usePresetMutations } from './price-preset-hooks';

const useGiftCardValueList = () =>
  usePresetList<IGiftCardValue>(
    'gift-card-value-list',
    GiftCardValueService.getGiftCardValueList,
    'setting.gift-card-value'
  );

/** Every active value; the endpoint has no "all", so a page large enough. */
const OPTION_FILTERS = { status: 'active', page: 1, limit: 100 };

/**
 * The company's gift card values as quick picks for the CMS gift card form,
 * smallest first, or the long-standing defaults while it has none. The
 * defaults also stand in while loading and on failure: a quick pick is a
 * shortcut, never a reason the form cannot be used.
 */
const useGiftCardValueOptions = () => {
  const { data, isPending } = useQuery({
    queryKey: ['gift-card-value-options', OPTION_FILTERS],
    queryFn: GiftCardValueService.getGiftCardValueList,
    select: res => res?.data,
    meta: { silentError: true },
  });

  const options = useMemo(() => toGiftCardValueOptions(data), [data]);
  return { options, isLoading: isPending };
};

type GiftCardValueInput = {
  companyId: string;
  value?: number | string | null;
  status: string;
};

const useGiftCardValueForm = () => {
  const { id } = useParams();
  const companyId = useAuthStore(state => state.user?.company?.id ?? '');

  const form = useForm<GiftCardValueInput, unknown, GiftCardValueValues>({
    resolver: yupResolver(giftCardValueValidationSchema) as Resolver<
      GiftCardValueInput,
      unknown,
      GiftCardValueValues
    >,
    defaultValues: { companyId, value: '', status: 'active' },
    mode: 'onChange',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['gift-card-value', id],
    queryFn: GiftCardValueService.getGiftCardValueById,
    enabled: !!id,
    select: res => res?.data,
  });

  const hydrated = useMemo<GiftCardValueInput | undefined>(
    () =>
      data && {
        companyId: data.companyId ?? companyId,
        value: data.value ?? '',
        status: data.status ?? 'active',
      },
    [data, companyId]
  );

  useEffect(() => {
    if (hydrated) form.reset(hydrated);
  }, [hydrated, form]);

  const mutations = usePresetMutations<GiftCardValueValues>({
    id,
    stored: hydrated && giftCardValueValidationSchema.cast(hydrated),
    create: GiftCardValueService.createGiftCardValue,
    edit: GiftCardValueService.editGiftCardValue,
    deletedMessage: 'Gift card value deleted',
    listRoute: '/setting/company/gift-card-values',
  });

  return {
    id,
    form,
    isLoading,
    handleReset: () => hydrated && form.reset(hydrated),
    ...mutations,
  };
};

export { useGiftCardValueList, useGiftCardValueOptions, useGiftCardValueForm };
